import { useState, useMemo } from "react";
import { Search, Table2 } from "lucide-react";
import { toast } from "sonner";
import { useSizing, countLinked } from "@/lib/sizing-store";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AssignOptionRow } from "../dialogs/AssignmentDialogShell";
import { BulkAssignmentShell } from "./BulkAssignmentShell";

/**
 * BulkLinkTableDialog — link many selected products to an existing sizing
 * table.  Special conflict wrinkle: a product may already be linked to a
 * DIFFERENT table (not just the target). In "empty-only" mode we skip both
 * cases (already-target or on-any-table).  In "overwrite" mode we relink
 * everything.
 */
export const BulkLinkTableDialog = ({ selectedProducts, links, onClose }) => {
    const { tables, linkProduct, unlinkProduct } = useSizing();
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("empty-only");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return tables;
        return tables.filter((t) =>
            t.name.toLowerCase().includes(q) ||
            (t.seriesLabel || "").toLowerCase().includes(q) ||
            (t.groupLabel || "").toLowerCase().includes(q)
        );
    }, [tables, query]);

    // Any product already linked to A table (not necessarily target).
    const conflicts = selectedProducts.filter((p) => !!links[p.id]);
    const conflictCount = conflicts.length;

    const apply = async (tableId) => {
        if (saving) return;
        const targets = mode === "empty-only"
            ? selectedProducts.filter((p) => !links[p.id])
            : selectedProducts;
        if (targets.length === 0) {
            toast.info("Etkilenecek ürün yok");
            return;
        }
        setSaving(true);
        try {
            const snapshot = targets.map((p) => ({ id: p.id, prevTableId: links[p.id] || null }));
            targets.forEach((p) => linkProduct(p.id, tableId));
            const tblName = tables.find((t) => t.id === tableId)?.name || "Tablo";
            toast.success(`${targets.length} ürün "${tblName}" tablosuna bağlandı`, {
                duration: 5000,
                action: {
                    label: "Geri Al",
                    onClick: () => {
                        snapshot.forEach((s) => {
                            if (s.prevTableId) linkProduct(s.id, s.prevTableId);
                            else unlinkProduct(s.id);
                        });
                        toast.success(`${snapshot.length} ürünün bağlantısı eski haline döndü`);
                    },
                },
            });
            onClose();
        } catch {
            toast.error("Toplu bağlama başarısız");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BulkAssignmentShell
            title="Mevcut tabloya bağla"
            count={selectedProducts.length}
            conflictCount={conflictCount}
            mode={mode}
            onModeChange={setMode}
            onClose={onClose}
            testId="bulk-link-table-dialog"
        >
            {tables.length > 0 && (
                <div className="sticky top-0 -mt-4 pt-3 pb-2 -mx-5 px-5 bg-muted z-10">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Tablo veya seri ara…"
                            className="h-9 pl-9 text-xs bg-background"
                            data-testid="bulk-link-table-search"
                        />
                    </div>
                </div>
            )}
            {tables.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">Henüz tablo yok.</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">Sonuç yok</div>
            ) : (
                filtered.map((t) => {
                    const cnt = countLinked(links, t.id);
                    return (
                        <AssignOptionRow
                            key={t.id}
                            current={false}
                            onClick={() => apply(t.id)}
                            disabled={saving}
                            testId={`bulk-link-table-option-${t.id}`}
                        >
                            <div className="h-10 w-10 rounded-md border border-border bg-muted grid place-items-center shrink-0">
                                <Table2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium truncate">{t.name}</span>
                                    {cnt > 0 && <Badge variant="outline" className="text-[9px] shrink-0">{cnt} ürün</Badge>}
                                </div>
                                <div className="text-[10.5px] text-muted-foreground truncate font-mono">
                                    {t.seriesLabel} · {t.groupLabel}
                                </div>
                            </div>
                        </AssignOptionRow>
                    );
                })
            )}
        </BulkAssignmentShell>
    );
};
